/* prettier-ignore */

import {
  CMS_STORAGE_PATH,
  IMAGE_VARIANT_WIDTHS,
  ITREM_IMAGE_HOST,
  ITREM_LARGE_SUFFIX_REGEX,
  ORIGINAL_DECLARED_WIDTH,
  PLACEHOLDER_FILENAME,
  VARIANT_SUFFIX_REGEX,
  WEBP_EXTENSION_REGEX,
} from '../config/constants';
import { IMAGE_SIZES_PRESETS } from '../config/image-sizes';
import { ImageSizesPreset, ImageVariantWidth, ResponsiveSources } from '../interfaces';
import { readConnectionMaxWidth } from './connection-quality';

/**
 * HRS-3471: single source of truth for CMS image-variant URLs, srcsets and
 * `sizes` strings. Pure functions (no DI) so services, NgModule components,
 * standalone components and non-Angular DOM (map overlays) can share them.
 */

function parseUrl(url: unknown): URL | null {
  if (typeof url !== 'string' || url.trim() === '') return null;
  try {
    return new URL(url, document.baseURI);
  } catch {
    return null;
  }
}

/** Absolute form of a URL (used to compare `img.src` with relative/absolute candidates). */
export function toAbsoluteUrl(url: string): string {
  return parseUrl(url)?.href ?? url;
}

/** True when the URL points to the CMS placeholder `default.webp`. */
export function isPlaceholderImage(url: unknown): boolean {
  const parsed = parseUrl(url);
  return !!parsed && parsed.pathname.split('/').pop() === PLACEHOLDER_FILENAME;
}

/** True when the URL is already a CMS width variant (`-w{N}.webp`). */
export function isVariantUrl(url: unknown): boolean {
  const parsed = parseUrl(url);
  return !!parsed && VARIANT_SUFFIX_REGEX.test(parsed.pathname);
}

/** True for CMS-processed originals: under `/storage/`, `.webp`, not a variant, not the placeholder. */
export function isVariantCapable(url: unknown): boolean {
  const parsed = parseUrl(url);
  if (!parsed) return false;

  const { pathname } = parsed;
  return pathname.includes(CMS_STORAGE_PATH)
    && WEBP_EXTENSION_REGEX.test(pathname)
    && !VARIANT_SUFFIX_REGEX.test(pathname)
    && pathname.split('/').pop() !== PLACEHOLDER_FILENAME;
}

/** HRS-2609: ITREM `{name}_g.jpg` -> `{name}.jpg` (its ~400px copy); `null` for any other URL. */
export function buildItremThumbnailUrl(url: unknown): string | null {
  const parsed = parseUrl(url);
  if (!parsed) return null;

  const isItremHost = parsed.hostname === ITREM_IMAGE_HOST || parsed.hostname.endsWith(`.${ITREM_IMAGE_HOST}`);
  if (!isItremHost || !ITREM_LARGE_SUFFIX_REGEX.test(parsed.pathname)) return null;

  parsed.pathname = parsed.pathname.replace(ITREM_LARGE_SUFFIX_REGEX, '$1');
  return parsed.href;
}

/** `{base}.webp` -> `{base}-w{N}.webp` (query string preserved); `null` when the URL has no variants. */
export function buildVariantUrl(original: unknown, width: ImageVariantWidth): string | null {
  if (!isVariantCapable(original)) return null;

  const parsed = parseUrl(original) as URL;
  parsed.pathname = parsed.pathname.replace(WEBP_EXTENSION_REGEX, `-w${width}.webp`);
  return parsed.href;
}

/**
 * Standard candidates capped by the smaller of the preset's own `maxWidth` and
 * the given (connection) `maxWidth`, plus the original as `1920w` for hero
 * presets when no cap applies. If the cap removes every candidate the smallest
 * one is kept. `null` when the URL has no variants.
 */
export function buildSrcset(original: unknown, preset: ImageSizesPreset, maxWidth?: number): string | null {
  if (!isVariantCapable(original)) return null;

  const { includeOriginal, maxWidth: presetMaxWidth } = IMAGE_SIZES_PRESETS[preset];
  const caps = [presetMaxWidth, maxWidth].filter((cap): cap is number => cap !== undefined);
  const cap = caps.length ? Math.min(...caps) : undefined;

  const allowed = IMAGE_VARIANT_WIDTHS.filter((width) => cap === undefined || width <= cap);
  const widths: readonly ImageVariantWidth[] = allowed.length ? allowed : [IMAGE_VARIANT_WIDTHS[0]];
  const candidates = widths.map((width) => `${buildVariantUrl(original, width)} ${width}w`);

  if (includeOriginal && cap === undefined) {
    candidates.push(`${toAbsoluteUrl(original as string)} ${ORIGINAL_DECLARED_WIDTH}w`);
  }

  return candidates.join(', ');
}

/** `sizes` attribute value of a preset. */
export function getImageSizes(preset: ImageSizesPreset): string {
  return IMAGE_SIZES_PRESETS[preset].sizes;
}

/** Error events already identified as a failed srcset variant by `handleResponsiveImageError`. */
const handledVariantErrors = new WeakSet<Event>();

/** True when the failing source of an image `error` event (or element) is a variant URL. */
export function isVariantLoadError(eventOrImg: Event | HTMLImageElement | null | undefined): boolean {
  if (eventOrImg instanceof Event && handledVariantErrors.has(eventOrImg)) return true;
  const target = eventOrImg instanceof Event ? eventOrImg.target : eventOrImg;
  if (!(target instanceof HTMLImageElement)) return false;
  return isVariantUrl(target.currentSrc || target.src);
}

/**
 * Drops `srcset`/`sizes` so the element falls back to its plain `src`.
 * Idempotent; used on any load error of an image that carries a srcset.
 */
export function clearResponsiveSources(img: HTMLImageElement): void {
  if (img.hasAttribute('srcset')) img.removeAttribute('srcset');
  if (img.hasAttribute('sizes')) img.removeAttribute('sizes');
}

/**
 * The only writer of an image's `src`, `srcset` and `sizes` (used by the
 * directive and `applyResponsiveImage`). The order matters in WebKit (measured
 * with Playwright WebKit 26.6): WebKit starts fetching `src` as soon as it is
 * set without a `srcset`, so `src` before `srcset` downloads the original AND
 * the variant, and removing `srcset` before writing the new `src` re-fetches
 * the previous original. Chromium waits for a stable state and is unaffected.
 * Hence:
 * - with a srcset: `sizes`, then `srcset`, then `src`;
 * - without one: `src` first, then `srcset`/`sizes` removed.
 * Unchanged values are not rewritten (a rewrite makes the browser select again).
 * Call it synchronously, never deferred: `AfterViewInit` consumers (e.g. Swiper)
 * must already see the final `src`.
 */
export function writeResponsiveSources(img: HTMLImageElement, { src, srcset, sizes }: ResponsiveSources): void {
  if (!srcset) {
    if (img.getAttribute('src') !== src) img.src = src;
    clearResponsiveSources(img);
    return;
  }

  if (sizes && img.getAttribute('sizes') !== sizes) img.setAttribute('sizes', sizes);
  if (img.getAttribute('srcset') !== srcset) img.setAttribute('srcset', srcset);
  if (img.getAttribute('src') !== src) img.src = src;
}

/**
 * `error` handler of the directive and `applyResponsiveImage`. A variant failure
 * is recorded BEFORE dropping the srcset: when the original is already in the
 * browser's memory cache, dropping the srcset switches `currentSrc` to it
 * synchronously, and host handlers running later for the same event would
 * otherwise mistake it for a failure of the original.
 */
export function handleResponsiveImageError(event: Event, img: HTMLImageElement): void {
  if (isVariantLoadError(event)) handledVariantErrors.add(event);
  clearResponsiveSources(img);
}

/**
 * Imperative equivalent of the `ciceroneResponsiveImage` directive for DOM
 * built outside Angular templates (Google Maps OverlayView). It owns the
 * element's `src`: the original is written after `sizes`/`srcset`, so callers
 * must not set `src` themselves. Non-variant-capable URLs only get their `src`.
 * A failed srcset candidate retries the original.
 */
export function applyResponsiveImage(img: HTMLImageElement, original: string, preset: ImageSizesPreset): void {
  const srcset = buildSrcset(original, preset, readConnectionMaxWidth());
  writeResponsiveSources(img, { src: original, srcset, sizes: srcset ? getImageSizes(preset) : null });
  if (srcset) img.addEventListener('error', (event) => handleResponsiveImageError(event, img));
}
